/**
 * Derive a Swiss-medication (SAI) search term from a matrix drug label.
 *
 * Shared by DrugSidePanel (desktop/iPad sidebar) and PairPicker (phone
 * inline detail) so both surfaces resolve the SAME record for a drug.
 *
 * Matrix labels are concentration-specific and may carry the generic in
 * parentheses — "Aggrastat (Tirofiban) 50 µg/ml". SAI name search matches on
 * the trade-name prefix, so we:
 *   1. strip the concentration tail (shortLabel),
 *   2. drop a "(generic)" parenthetical → the trade name ("Aggrastat"),
 *   3. drop a hyphenated salt suffix → the base substance
 *      ("Noradrenalin-Bitartrat" → "Noradrenalin", "Piperacillin-Tazobactam"
 *      → "Piperacillin"). SAI indexes the base substance, not the salt form,
 *      so the base prefix is what resolves. Single-word trade names are
 *      unaffected (Dexdor/Phenhydan carry their hyphen INSIDE the parenthetical,
 *      already removed in step 2).
 * Pure-generic labels ("Fentanyl 50 µg/ml") reduce to the generic ("Fentanyl"),
 * which SAI resolves via its trade-name or ingredient tier. A term that
 * resolves to nothing renders a plain "not found" card — never a fabrication.
 */

import { MIN_TERM_LEN } from '@frontend/hooks/useMedicationSearch'
import type { CompatibilityDrug } from '@frontend/components/ysite/types'
import { shortLabel } from '@frontend/components/ysite/label'

export function drugSearchTerm(drug: CompatibilityDrug): string {
  const name = shortLabel(drug)
  const paren = name.indexOf(' (')
  const tradeName = (paren > 0 ? name.slice(0, paren) : name).trim()
  // Only trust a hyphen-stripped base if it exceeds the SAI minimum term
  // length. A 2-char prefix ("Co-trimoxazol" → "Co") still passes the SAI
  // floor but is too ambiguous and would surface an UNRELATED drug's record —
  // a clinical hazard. Fall back to the full trade name there, which resolves
  // to a clean "not found" rather than the wrong drug.
  const base = tradeName.split('-')[0].trim()
  return base.length > MIN_TERM_LEN ? base : tradeName
}
